import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, TextField, Tooltip } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { DynamicFormDefinition } from "src/common"
import hljs from "highlight.js";
import { DependencyInjectionContext } from "../../../contexts/dependency-injection-context";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const FormDetailsModal = ({ form, isOpen, closeForm, saveForm }: {
    form: DynamicFormDefinition,
    isOpen: boolean,
    closeForm: () => void,
    saveForm: (form: DynamicFormDefinition) => void
}) => {
    const copyToClip = "Copy to clipboard";
    const copied = "Copied";

    const [open, setOpen] = useState<boolean>(isOpen);
    const [formName, setFormName] = useState<string>(form.name);
    const [isWeb, setIsWeb] = useState<boolean>(form.isWebForm);
    const [isMobile, setIsMobile] = useState<boolean>(form.isOfflineForm);
    const [showReturn, setShowReturn] = useState<boolean | undefined>(form.successMessage?.showReturnButton);
    const [successHtml, setSuccessHtml] = useState<string | undefined>(form.successMessage?.html);
    const [successRedirect, setSuccessRedirect] = useState<string | undefined>(form.successMessage?.redirectUrl);
    const [successCallback, setSuccessCallback] = useState<string | undefined>(form.successMessage?.javascriptCallbackName);
    const [includeCode, setIncludeCode] = useState<string>();
    const [copyTooltip, setCopyTooltip] = useState<string>(copyToClip);

    const { applicationState } = useContext(DependencyInjectionContext);
    

    useEffect(() => {
        setIncludeCode(`<script defer="defer" src="${process.env.REACT_APP_WEB_JS_URL}?clientId=${applicationState.selectedClientId}&formDefinitionId=${form.id}"></script>`);
        setOpen(isOpen);
        setTimeout(() => hljs.highlightAll(), 100);
    }, [isOpen]);

    const handleCancel = () => {
        closeForm();
        resetForm();
    };

    const handleOk = () => {
        form.isOfflineForm = isMobile;
        form.isWebForm = isWeb;
        form.name = formName;

        if (successHtml || successRedirect || successCallback) {
            form.successMessage = {
                html: successHtml,
                showReturnButton: showReturn,
                javascriptCallbackName: successCallback,
                redirectUrl: successRedirect
            }
        }

        saveForm(form);
        closeForm();
    }

    function resetForm(): void {
        setFormName(form.name);
        setIsWeb(form.isWebForm);
        setIsMobile(form.isOfflineForm);
        setShowReturn(form.successMessage?.showReturnButton);
        setSuccessHtml(form.successMessage?.html);
    }

    function copyToClipboard(): void {
        setCopyTooltip(copied);
        navigator.clipboard.writeText(includeCode || "");
    }

    function changeTooltipText(newText: string): void {
        setTimeout(() => setCopyTooltip(newText), 100);
    }   

    return (
        <Dialog
            open={open}
        >
            <DialogTitle>Form Details</DialogTitle>
            <DialogContent>
                <Grid container spacing={3} direction="row" justifyContent="space-between" alignItems="center" style={{ paddingTop: '10px' }}>
                    <Grid item xs={12}>
                        <TextField
                            id="form-name"
                            label="Form Name"
                            fullWidth
                            required
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="form-name"
                            label="Success Message HTML"
                            helperText="Message to display on form submission. Can be an HTML string."
                            fullWidth
                            value={successHtml}
                            onChange={(e) => setSuccessHtml(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="form-name"
                            label="Success Redirect URL"
                            helperText="URL to navigate to on form submission"
                            fullWidth
                            value={successRedirect}
                            onChange={(e) => setSuccessRedirect(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="form-name"
                            label="Success Javascript Callback"
                            helperText="Name of a javascript function to call on form submission"
                            fullWidth
                            value={successCallback}
                            onChange={(e) => setSuccessCallback(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox checked={showReturn} onChange={(e) => setShowReturn(e.target.checked)} />} label="Show Return Button on Submission" />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox checked={isWeb} onChange={(e) => setIsWeb(e.target.checked)} />} label="Web Form" />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox checked={isMobile} onChange={(e) => setIsMobile(e.target.checked)} />} label="Mobile Form" />
                    </Grid>
                    <Grid item xs={12} style={{ position: "relative" }}>
                        <h5>Include Script</h5>
                        <pre>
                            <code className="language-html">
                                {includeCode}
                            </code>
                        </pre>
                        <Tooltip title={copyTooltip} onClose={() => changeTooltipText(copyToClip)}>
                            <IconButton style={{ position: "absolute", top: "3rem", right: 0 }} aria-label="return" onClick={copyToClipboard}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleOk}>Ok</Button>
            </DialogActions>
        </Dialog>
    );

};

export default FormDetailsModal;